import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate, uploadFile } from '../shared/api';

type Locale = 'hr' | 'en';

type Product = {
    id: number;
    sku: string | null;
    name_hr: string;
    name_en: string | null;
    fine_weight_g: number | null;
    gross_weight_g: number | null;
    purity: number | null;
    metal_type: string | null;
    length_mm: number | null;
    width_mm: number | null;
    thickness_mm: number | null;
    diameter_mm: number | null;
    dimensions_note: string | null;
    blister_length_mm: number | null;
    blister_width_mm: number | null;
    image_url: string | null;
    image_path: string | null;
    image_src: string | null;
    // Varchar mirror (legacy) — read-only iz šifrarnika preko backfill komande
    cgm_category: string | null;
    cgm_warehouse: string | null;
    cgm_mint: string | null;
    cgm_supplier: string | null;
    // FK polja (izvor istine) + JOIN-ana kanonska imena iz backend-a
    mint_partner_id: number | null;
    primary_supplier_partner_id: number | null;
    category_id: number | null;
    delivery_type_id: number | null;
    warehouse_type_id: number | null;
    weight_class_id: number | null;
    product_type_id: number | null;
    mint_partner_name: string | null;
    supplier_partner_name: string | null;
    category_name: string | null;
    delivery_type_name: string | null;
    warehouse_type_name: string | null;
    weight_class_name: string | null;
    product_type_name: string | null;
    is_active: 0 | 1 | boolean;
    updated_at: string;
};

type Stats = { total: number; active: number; inactive: number; with_cgm: number };

type ListResp = { rows: Product[]; stats: Stats };

type CodebookOption = { id: number; name: string };
type WeightClassOption = CodebookOption & { fine_weight_g: number };

type Codebooks = {
    mints: CodebookOption[];
    suppliers: CodebookOption[];
    categories: CodebookOption[];
    delivery_types: CodebookOption[];
    warehouse_types: CodebookOption[];
    weight_classes: WeightClassOption[];
    product_types: CodebookOption[];
};

export function AdminProductsSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<ListResp | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('active');
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [editProduct, setEditProduct] = useState<Product | null>(null);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const qs = new URLSearchParams();
            if (search) qs.set('q', search);
            if (filter === 'active') qs.set('only_active', '1');
            if (filter === 'inactive') qs.set('only_inactive', '1');
            setData(await api<ListResp>(`/admin/products?${qs.toString()}`, token));
        } catch (e) {
            setError(e instanceof Error ? e.message : 'API');
        } finally {
            setLoading(false);
        }
    }, [token, filter, search]);

    useEffect(() => { void load(); }, [load]);

    async function toggleActive(id: number, currentActive: boolean) {
        try {
            await mutate(`/admin/products/${id}/active`, token, 'PATCH', { is_active: !currentActive });
            await load();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'API');
        }
    }

    async function bulkSetActive(active: boolean) {
        if (selected.size === 0) return;
        if (!confirm(t(`Postaviti ${selected.size} produkata kao ${active ? 'AKTIVNE' : 'NEAKTIVNE'}?`, `Set ${selected.size} products as ${active ? 'ACTIVE' : 'INACTIVE'}?`))) return;
        try {
            await mutate('/admin/products/bulk-active', token, 'POST', { ids: Array.from(selected), is_active: active });
            setSelected(new Set());
            await load();
        } catch (e) {
            alert(e instanceof Error ? e.message : 'API');
        }
    }

    function toggleSelect(id: number) {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id); else next.add(id);
        setSelected(next);
    }

    if (loading && !data) return <p className="muted">{t('Učitavam…', 'Loading…')}</p>;
    if (error) return <p className="error">{error}</p>;
    if (!data) return null;

    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">📦</div>
                <div className="bi-module-hero-text">
                    <h1>130 · {t('Šifrarnik produkata', 'Products catalog')}</h1>
                    <p className="muted">{t(
                        'Master lista produkata. Neaktivni produkti se ne pokazuju u 40.1 Cijene/Pravila i ne ulaze u algoritme. Mogu ostati u sustavu zbog povijesti.',
                        'Master products list. Inactive products are hidden from 40.1 Pricing/Rules and excluded from algorithms. Kept in system for history.',
                    )}</p>
                </div>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 14 }}>
                <div style={{ background: '#fff', border: '1px solid rgba(var(--ad-soft-rgb),0.3)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(0,0,0,0.5)' }}>{t('Ukupno', 'Total')}</div>
                    <strong style={{ fontSize: '1.1rem' }}>{data.stats.total}</strong>
                </div>
                <div style={{ background: 'rgba(31,122,54,0.08)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#1f7a36' }}>{t('Aktivni', 'Active')}</div>
                    <strong style={{ fontSize: '1.1rem', color: '#1f7a36' }}>{data.stats.active}</strong>
                </div>
                <div style={{ background: 'rgba(177,64,64,0.08)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#b14040' }}>{t('Neaktivni', 'Inactive')}</div>
                    <strong style={{ fontSize: '1.1rem', color: '#b14040' }}>{data.stats.inactive}</strong>
                </div>
                <div style={{ background: 'rgba(var(--ad-acc-rgb),0.10)', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ad-acc-3)' }}>{t('S CGM kategorijom', 'With CGM category')}</div>
                    <strong style={{ fontSize: '1.1rem', color: 'var(--ad-acc-3)' }}>{data.stats.with_cgm}</strong>
                </div>
            </div>

            {/* Filters */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('Traži po nazivu / SKU / dobavljaču…', 'Search by name / SKU / supplier…')}
                    style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', fontSize: '0.86rem', minWidth: 280 }}
                />
                <div style={{ display: 'flex', gap: 4 }}>
                    {(['all', 'active', 'inactive'] as const).map((f) => (
                        <button key={f} type="button" onClick={() => setFilter(f)} className={filter === f ? '' : 'secondary compact'} style={{ fontSize: '0.78rem' }}>
                            {f === 'all' ? t('Sve', 'All') : f === 'active' ? '✓ ' + t('Aktivni', 'Active') : '✗ ' + t('Neaktivni', 'Inactive')}
                        </button>
                    ))}
                </div>
                {selected.size > 0 ? (
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, alignItems: 'center' }}>
                        <span className="muted" style={{ fontSize: '0.82rem' }}>{selected.size} {t('označeno', 'selected')}</span>
                        <button type="button" className="compact" onClick={() => void bulkSetActive(true)} style={{ fontSize: '0.78rem', background: '#1f7a36', color: '#fff' }}>✓ {t('Postavi aktivno', 'Set active')}</button>
                        <button type="button" className="secondary compact" onClick={() => void bulkSetActive(false)} style={{ fontSize: '0.78rem', color: '#b14040' }}>✗ {t('Postavi neaktivno', 'Set inactive')}</button>
                    </div>
                ) : null}
            </div>

            <div className="table-wrap">
                <table className="admin-user-table" style={{ fontSize: '0.84rem' }}>
                    <thead>
                        <tr>
                            <th style={{ width: 30, textAlign: 'center' }}>
                                <input type="checkbox" checked={data.rows.length > 0 && selected.size === data.rows.length} onChange={() => {
                                    if (selected.size === data.rows.length) setSelected(new Set());
                                    else setSelected(new Set(data.rows.map((r) => r.id)));
                                }} style={{ accentColor: 'var(--ad-acc)' }} />
                            </th>
                            <th style={{ width: 44 }}>{t('Slika', 'Image')}</th>
                            <th>{t('SKU', 'SKU')}</th>
                            <th>{t('Naziv', 'Name')}</th>
                            <th>{t('Kovnica', 'Mint')}</th>
                            <th>{t('Dobavljač', 'Supplier')}</th>
                            <th>{t('Kategorija', 'Category')}</th>
                            <th>{t('Tip', 'Type')}</th>
                            <th style={{ textAlign: 'right' }}>{t('Fine g', 'Fine g')}</th>
                            <th>{t('Status', 'Status')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((p) => {
                            const active = !!p.is_active;
                            const isSelected = selected.has(p.id);
                            return (
                                <tr key={p.id} style={{ ...(isSelected ? { background: 'rgba(var(--ad-acc-rgb),0.08)' } : {}), ...(active ? {} : { opacity: 0.55 }) }}>
                                    <td style={{ textAlign: 'center' }}>
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(p.id)} style={{ accentColor: 'var(--ad-acc)' }} />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        {p.image_src ? (
                                            <span style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }} title={t('Fotka dodijeljena', 'Photo assigned')}>
                                                <img src={p.image_src} alt="" style={{ width: 34, height: 34, objectFit: 'contain', borderRadius: 4, background: '#fff', border: '2px solid #1f7a36' }} />
                                                <span style={{ position: 'absolute', top: -5, right: -5, background: '#1f7a36', color: '#fff', borderRadius: '50%', width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 800 }}>✓</span>
                                            </span>
                                        ) : (
                                            <span title={t('Nema fotke — dodijeli u uređivanju', 'No photo — assign in edit')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(177,64,64,0.12)', color: '#b14040', padding: '4px 8px', borderRadius: 6, fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>📷 {t('NEMA', 'NONE')}</span>
                                        )}
                                    </td>
                                    <td style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: '0.78rem' }}>{p.sku ?? '—'}</td>
                                    <td><strong>{p.name_hr}</strong></td>
                                    <td style={{ fontSize: '0.78rem' }}>{p.mint_partner_name ?? <span className="muted" title={t('Postavi u Uredi', 'Set in Edit')}>—</span>}</td>
                                    <td style={{ fontSize: '0.78rem' }}>{p.supplier_partner_name ?? <span className="muted" title={t('Postavi u Uredi', 'Set in Edit')}>—</span>}</td>
                                    <td style={{ fontSize: '0.76rem' }}>{p.category_name ?? <span className="muted">—</span>}</td>
                                    <td style={{ fontSize: '0.78rem' }}>{p.product_type_name ?? <span className="muted">—</span>}</td>
                                    <td style={{ textAlign: 'right' }}>{p.fine_weight_g ?? '—'}</td>
                                    <td>
                                        {active ? (
                                            <span style={{ background: 'rgba(31,122,54,0.15)', color: '#1f7a36', padding: '2px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>✓ {t('Aktivan', 'Active')}</span>
                                        ) : (
                                            <span style={{ background: 'rgba(177,64,64,0.10)', color: '#b14040', padding: '2px 8px', borderRadius: 6, fontSize: '0.74rem', fontWeight: 700 }}>✗ {t('Neaktivan', 'Inactive')}</span>
                                        )}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <button type="button" className="secondary compact" onClick={() => setEditProduct(p)} style={{ fontSize: '0.72rem', padding: '2px 8px', marginRight: 4 }}>
                                            ✎ {t('Uredi', 'Edit')}
                                        </button>
                                        <button type="button" className="secondary compact" onClick={() => void toggleActive(p.id, active)} style={{ fontSize: '0.72rem', padding: '2px 8px' }}>
                                            {active ? '✗ ' + t('Deaktiviraj', 'Deactivate') : '✓ ' + t('Aktiviraj', 'Activate')}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <p className="muted" style={{ fontSize: '0.74rem', marginTop: 10, fontStyle: 'italic' }}>
                💡 {t(
                    'Neaktivni produkti se sakrivaju iz m.40.1 Cijene tabu po defaultu. Šifre i podaci se NE brišu — produkt se može aktivirati ponovo.',
                    'Inactive products are hidden from m.40.1 Pricing tab by default. SKUs and data are NOT deleted — product can be re-activated.',
                )}
            </p>

            {editProduct ? (
                <ProductEditModal
                    token={token}
                    locale={locale}
                    product={editProduct}
                    onClose={() => setEditProduct(null)}
                    onSaved={async () => { setEditProduct(null); await load(); }}
                />
            ) : null}
        </section>
    );
}

function dimsLabel(p: Product): string | null {
    if (p.diameter_mm) return `⌀ ${p.diameter_mm}${p.thickness_mm ? ` × ${p.thickness_mm}` : ''} mm`;
    if (p.length_mm || p.width_mm || p.thickness_mm) {
        return [p.length_mm, p.width_mm, p.thickness_mm].filter((x) => x != null).join(' × ') + ' mm';
    }
    return p.dimensions_note || null;
}

function ProductEditModal({ token, locale, product, onClose, onSaved }: {
    token: string; locale: Locale; product: Product; onClose: () => void; onSaved: () => Promise<void>;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [form, setForm] = useState({
        name_hr: product.name_hr ?? '',
        name_en: product.name_en ?? '',
        metal_type: product.metal_type ?? '',
        gross_weight_g: product.gross_weight_g ?? '',
        fine_weight_g: product.fine_weight_g ?? '',
        purity: product.purity ?? '',
        length_mm: product.length_mm ?? '',
        width_mm: product.width_mm ?? '',
        thickness_mm: product.thickness_mm ?? '',
        diameter_mm: product.diameter_mm ?? '',
        dimensions_note: product.dimensions_note ?? '',
        blister_length_mm: product.blister_length_mm ?? '',
        blister_width_mm: product.blister_width_mm ?? '',
        // FK polja iz šifrarnika
        mint_partner_id: product.mint_partner_id ?? '',
        primary_supplier_partner_id: product.primary_supplier_partner_id ?? '',
        category_id: product.category_id ?? '',
        delivery_type_id: product.delivery_type_id ?? '',
        warehouse_type_id: product.warehouse_type_id ?? '',
        weight_class_id: product.weight_class_id ?? '',
        product_type_id: product.product_type_id ?? '',
    });
    const [imageSrc, setImageSrc] = useState<string | null>(product.image_src);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [codebooks, setCodebooks] = useState<Codebooks | null>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const cb = await api<Codebooks>('/admin/products/codebooks', token);
                if (!cancelled) setCodebooks(cb);
            } catch (e) {
                if (!cancelled) setErr(e instanceof Error ? e.message : 'API');
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const setF = (k: keyof typeof form, v: string) => setForm((s) => ({ ...s, [k]: v }));
    const numOrNull = (v: string | number) => (v === '' || v === null ? null : Number(v));
    const idOrNull = (v: string | number) => (v === '' || v === null ? null : Number(v));

    const save = async () => {
        setBusy(true); setErr('');
        try {
            await mutate(`/admin/products/${product.id}`, token, 'PATCH', {
                name_hr: form.name_hr, name_en: form.name_en || null, metal_type: form.metal_type || null,
                gross_weight_g: numOrNull(form.gross_weight_g), fine_weight_g: numOrNull(form.fine_weight_g),
                purity: numOrNull(form.purity), length_mm: numOrNull(form.length_mm), width_mm: numOrNull(form.width_mm),
                thickness_mm: numOrNull(form.thickness_mm), diameter_mm: numOrNull(form.diameter_mm),
                dimensions_note: form.dimensions_note || null,
                blister_length_mm: numOrNull(form.blister_length_mm), blister_width_mm: numOrNull(form.blister_width_mm),
                // FK polja
                mint_partner_id: idOrNull(form.mint_partner_id),
                primary_supplier_partner_id: idOrNull(form.primary_supplier_partner_id),
                category_id: idOrNull(form.category_id),
                delivery_type_id: idOrNull(form.delivery_type_id),
                warehouse_type_id: idOrNull(form.warehouse_type_id),
                weight_class_id: idOrNull(form.weight_class_id),
                product_type_id: idOrNull(form.product_type_id),
            });
            await onSaved();
        } catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const onPickImage = async (file: File | null) => {
        if (!file) return;
        setBusy(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('image', file);
            const r = await uploadFile<{ ok: boolean; image_src: string }>(`/admin/products/${product.id}/image`, token, fd);
            setImageSrc(r.image_src);
        } catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setBusy(false); }
    };

    const numField = (k: keyof typeof form, label: string) => (
        <label style={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="muted">{label}</span>
            <input type="number" step="0.01" value={form[k] as string} onChange={(e) => setF(k, e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }} />
        </label>
    );

    const selectField = (k: keyof typeof form, label: string, opts: { id: number; name: string }[] | undefined) => (
        <label style={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span className="muted">{label}</span>
            <select
                value={String(form[k] ?? '')}
                disabled={!opts}
                onChange={(e) => setF(k, e.target.value)}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: '#fff', fontSize: '0.84rem' }}
            >
                <option value="">{t('— nepostavljeno —', '— unset —')}</option>
                {(opts ?? []).map((o) => (
                    <option key={o.id} value={String(o.id)}>{o.name}</option>
                ))}
            </select>
        </label>
    );

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1500, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '4vh 12px', overflowY: 'auto' }}>
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(640px, 96vw)', background: '#fffdf6', borderRadius: 12, overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
                <div style={{ background: 'linear-gradient(135deg, var(--ad-ink), var(--ad-acc))', padding: '14px 20px', color: '#fffbeb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <p style={{ margin: 0, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.14em', opacity: 0.8 }}>{t('Uredi proizvod', 'Edit product')} · {product.sku ?? product.id}</p>
                        <h2 style={{ margin: '2px 0 0', fontSize: '1.1rem' }}>{product.name_hr}</h2>
                    </div>
                    <button type="button" onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer', padding: '4px 12px', fontSize: '1.05rem' }}>✕</button>
                </div>
                <div style={{ padding: 18, display: 'grid', gap: 14 }}>
                    {err ? <p className="error" style={{ margin: 0 }}>{err}</p> : null}
                    {/* Slika */}
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                        <div style={{ width: 90, height: 90, borderRadius: 8, border: '1px solid rgba(var(--ad-soft-rgb),0.4)', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {imageSrc ? <img src={imageSrc} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: '1.6rem', opacity: 0.4 }}>🖼</span>}
                        </div>
                        <label style={{ fontSize: '0.82rem' }}>
                            📷 {t('Slika proizvoda', 'Product image')}
                            <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(e) => void onPickImage(e.target.files?.[0] ?? null)} style={{ display: 'block', marginTop: 4, fontSize: '0.8rem' }} />
                            <span className="muted" style={{ fontSize: '0.72rem' }}>{t('JPG/PNG/WEBP, sprema se odmah (resize ≤900px).', 'JPG/PNG/WEBP, saved immediately (resize ≤900px).')}</span>
                        </label>
                    </div>
                    {/* Naziv + metal */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <label style={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span className="muted">{t('Naziv (HR)', 'Name (HR)')}</span>
                            <input value={form.name_hr} onChange={(e) => setF('name_hr', e.target.value)} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }} />
                        </label>
                        <label style={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span className="muted">{t('Metal', 'Metal')}</span>
                            <input value={form.metal_type} onChange={(e) => setF('metal_type', e.target.value)} placeholder="gold / silver" style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }} />
                        </label>
                    </div>
                    {/* Težine + čistoća */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                        {numField('gross_weight_g', t('Bruto g', 'Gross g'))}
                        {numField('fine_weight_g', t('Fini g', 'Fine g'))}
                        {numField('purity', t('Čistoća', 'Purity'))}
                    </div>
                    {/* Dimenzije */}
                    <div>
                        <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ad-ink)', fontWeight: 700, marginBottom: 6 }}>📐 {t('Dimenzije (mm)', 'Dimensions (mm)')}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                            {numField('length_mm', t('Dužina', 'Length'))}
                            {numField('width_mm', t('Širina', 'Width'))}
                            {numField('thickness_mm', t('Debljina', 'Thickness'))}
                            {numField('diameter_mm', t('Promjer', 'Diameter'))}
                        </div>
                        <label style={{ fontSize: '0.8rem', display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
                            <span className="muted">{t('Napomena (slobodno)', 'Note (free text)')}</span>
                            <input value={form.dimensions_note} onChange={(e) => setF('dimensions_note', e.target.value)} placeholder={t('npr. blister 27 × 47 mm', 'e.g. blister 27 × 47 mm')} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid rgba(var(--ad-soft-rgb),0.4)' }} />
                        </label>
                        <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ad-ink)', fontWeight: 700, marginBottom: 6 }}>📦 {t('Dimenzije blistera (mm)', 'Blister dimensions (mm)')}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                {numField('blister_length_mm', t('Dužina', 'Length'))}
                                {numField('blister_width_mm', t('Širina', 'Width'))}
                            </div>
                        </div>
                    </div>
                    {/* Šifrarnička klasifikacija — sve preko FK na šifrarničke tablice. */}
                    <div>
                        <div style={{ fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ad-ink)', fontWeight: 700, marginBottom: 6 }}>
                            🗂 {t('Šifrarnička klasifikacija', 'Codebook classification')}
                            {!codebooks ? <span className="muted" style={{ fontSize: '0.7rem', marginLeft: 8, textTransform: 'none' }}>{t('učitavam…', 'loading…')}</span> : null}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            {selectField('mint_partner_id', t('Kovnica', 'Mint'), codebooks?.mints)}
                            {selectField('primary_supplier_partner_id', t('Glavni dobavljač', 'Primary supplier'), codebooks?.suppliers)}
                            {selectField('category_id', t('Kategorija', 'Category'), codebooks?.categories)}
                            {selectField('delivery_type_id', t('Vrsta isporuke', 'Delivery type'), codebooks?.delivery_types)}
                            {selectField('warehouse_type_id', t('Vrsta skladišta', 'Warehouse type'), codebooks?.warehouse_types)}
                            {selectField('weight_class_id', t('Težinska klasa', 'Weight class'), codebooks?.weight_classes)}
                            {selectField('product_type_id', t('Tip proizvoda', 'Product type'), codebooks?.product_types)}
                        </div>
                        <p className="muted" style={{ fontSize: '0.7rem', margin: '6px 0 0', fontStyle: 'italic' }}>
                            💡 {t(
                                'Sve vrijednosti dolaze iz šifrarnika (bi_partners, bi_product_categories itd.). Ako fali stavka — dodaj je u zasebnom admin podmodulu za šifrarnike. Stari "cgm_*" tekst polja se automatski ažuriraju na kanonsko ime prilikom spremanja.',
                                'All values come from codebooks (bi_partners, bi_product_categories, etc.). If a value is missing — add it via the codebook admin sub-module. Legacy "cgm_*" text fields auto-update to canonical names on save.',
                            )}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button type="button" className="secondary" onClick={onClose}>{t('Zatvori', 'Close')}</button>
                        <button type="button" disabled={busy} onClick={() => void save()} style={{ background: '#1f7a36', color: '#fff', border: 0, padding: '8px 18px', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                            {busy ? '⏳' : '✓ ' + t('Spremi', 'Save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
